import { analyticsSendDesignEvent } from '@helpers/engine/analytics';
import { updateGamestate, worldTownsState } from '@helpers/state-game';
import type {
  GameState,
  TownId,
  TownReputationGainResult,
  TownReputationGainSource,
} from '@interfaces';
import { clamp } from 'es-toolkit/compat';
import { taskEventTownReputationTier } from '@helpers/task/task-events';

// Cumulative reputation needed to reach each tier (0 = Neutral .. 4 = Renowned) - a shared game-balance curve, not authored per-town.
export const TOWN_REPUTATION_THRESHOLDS: Record<number, number> = {
  0: 0,
  1: 100,
  2: 600,
  3: 2100,
  4: 7100,
};

export const TOWN_REPUTATION_MAX = TOWN_REPUTATION_THRESHOLDS[4];

export const TOWN_REPUTATION_TIER_NAMES: Record<number, string> = {
  0: 'Neutral',
  1: 'Friendly',
  2: 'Honored',
  3: 'Distinguished',
  4: 'Renowned',
};

const TIERS_DESCENDING = [4, 3, 2, 1, 0];

export function townReputationTierForAmount(reputation: number): number {
  return (
    TIERS_DESCENDING.find(
      (tier) => reputation >= TOWN_REPUTATION_THRESHOLDS[tier],
    ) ?? 0
  );
}

export function townReputationTierName(tier: number): string {
  return TOWN_REPUTATION_TIER_NAMES[tier] ?? 'Neutral';
}

// Pass `state` from inside an updateGamestate callback - the selector can be stale there.
export function townReputation(townId: TownId, state?: GameState): number {
  const towns = state ? state.world.towns : worldTownsState();
  return towns[townId]?.reputation ?? 0;
}

export function townReputationTier(townId: TownId, state?: GameState): number {
  return townReputationTierForAmount(townReputation(townId, state));
}

// `gained` is after clamping to the max; `tierChanged` lets callers re-sync the town's buff immediately.
// Awaits its own updateGamestate call so a non-tick caller sees the mutation land before checking the result.
export async function townReputationGain(
  townId: TownId,
  amount: number,
  source: TownReputationGainSource,
): Promise<TownReputationGainResult> {
  if (amount <= 0) return { gained: 0, tierChanged: false };

  let applied = false;
  let gained = 0;
  let tierChanged = false;
  let newTier = 0;

  await updateGamestate((state) => {
    const town = state.world.towns[townId];
    if (!town) return state;

    const reputation = clamp(town.reputation + amount, 0, TOWN_REPUTATION_MAX);
    newTier = townReputationTierForAmount(reputation);
    tierChanged = newTier !== townReputationTierForAmount(town.reputation);
    gained = reputation - town.reputation;
    town.reputation = reputation;
    applied = true;

    return state;
  });

  if (!applied) return { gained: 0, tierChanged: false };

  analyticsSendDesignEvent(`Town:Reputation:${source}`);
  if (tierChanged) void taskEventTownReputationTier(townId, newTier);

  return { gained, tierChanged };
}

// The sole subtraction path - reputation is otherwise cumulative-only, reserved for a raid loss.
// Returns whether the loss crossed a tier threshold - see townReputationGain for the async/tick-safety note.
export async function townReputationLose(
  townId: TownId,
  amount: number,
  source: TownReputationGainSource,
): Promise<boolean> {
  if (amount <= 0) return false;

  let applied = false;
  let tierChanged = false;

  await updateGamestate((state) => {
    const town = state.world.towns[townId];
    if (!town) return state;

    const reputation = clamp(town.reputation - amount, 0, TOWN_REPUTATION_MAX);
    tierChanged =
      townReputationTierForAmount(reputation) !==
      townReputationTierForAmount(town.reputation);
    town.reputation = reputation;
    applied = true;

    return state;
  });

  if (!applied) return false;

  analyticsSendDesignEvent(`Town:Reputation:Lose:${source}`);

  return tierChanged;
}

// Highest-defined tier at or below the current one - lets a table author only some tiers and still resolve sensibly below that.
export function townReputationTierMultiplier<T>(
  tier: number,
  table: Partial<Record<number, T>>,
): T | undefined {
  for (let t = tier; t >= 0; t--) {
    if (table[t] !== undefined) return table[t];
  }
  return undefined;
}
